import type { ResumableClientStorage } from '@assistant-ui/react-ai-sdk';

// The runtime's resume effect runs again as chat state changes. Only a stream
// left by an earlier mount is eligible: exposing the current POST's newly
// persisted id would start a second SDK reader for the same live response.
// Keep this adapter stable for the lifetime of a thread's mounted runtime.
/** The resume storage plus a compare-and-clear: forget a stream only while it is STILL the current one. */
export type ResumeStorage = ResumableClientStorage & {
  clearIfCurrent?: (...expected: string[]) => void;
  adoptAtMount?: (streamId: string) => void;
  preventDiscovery?: () => void;
};

export function createMountResumeStorage(storage: ResumableClientStorage): ResumeStorage {
  let pendingAtMount = storage.getStreamId();
  let submitted = false;
  return {
    getStreamId: () => pendingAtMount,
    // Persist current turns for the next reload, without making them eligible
    // for this mount's resume effect. Mid-read recovery stays in resilient-fetch.
    setStreamId: (id) => {
      submitted = true;
      storage.setStreamId(id);
    },
    adoptAtMount: (id) => {
      if (submitted || pendingAtMount !== null) return;
      pendingAtMount = id;
      storage.setStreamId(id);
    },
    preventDiscovery: () => {
      submitted = true;
      pendingAtMount = null;
    },
    clear: () => {
      pendingAtMount = null;
      storage.clear();
    },
    // A delayed cleanup of an earlier original must not erase the descriptor a newer original has since stored.
    // The mount snapshot above is not the persisted current id, so the comparison reads the storage itself.
    clearIfCurrent: (...expected) => {
      const current = storage.getStreamId();
      if (current === null || !expected.includes(current)) return;
      if (pendingAtMount === current) pendingAtMount = null;
      storage.clear();
    },
  };
}
